import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from './Modal';
import CommandPalette from './CommandPalette';
import './overlays.css';

const OverlayContext = createContext(null);

export function useOverlays() {
  const ctx = useContext(OverlayContext);
  if (!ctx) throw new Error('useOverlays must be used inside <OverlayProvider>');
  return ctx;
}

let toastSeq = 0;

/**
 * Owns the app-level overlay state the handoff calls for: `toasts`, `modal`, `paletteOpen`.
 *
 * `confirm()` returns a promise so replacing a SweetAlert is a direct swap:
 *   if (await confirm({ title: 'Delete coupon?', body: '…', destructive: true })) { … }
 */
export default function OverlayProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const resolverRef = useRef(null);

  const dismissToast = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message, { tone = 'default', duration = 4000 } = {}) => {
      toastSeq += 1;
      const id = toastSeq;
      setToasts((list) => [...list, { id, message, tone }]);
      if (duration > 0) {
        setTimeout(() => dismissToast(id), duration);
      }
      return id;
    },
    [dismissToast]
  );

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setConfirmState({ ...options, loading: false });
    });
  }, []);

  const settle = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setConfirmState(null);
    if (resolve) resolve(result);
  }, []);

  const handleConfirm = useCallback(async () => {
    // If the caller supplied an async action, keep the modal open with a spinner
    // until it settles, so the row cannot be actioned twice.
    const action = confirmState?.action;
    if (!action) {
      settle(true);
      return;
    }
    setConfirmState((s) => ({ ...s, loading: true }));
    try {
      await action();
      settle(true);
    } catch (err) {
      settle(false);
    }
  }, [confirmState, settle]);

  const value = useMemo(
    () => ({
      toast,
      confirm,
      dismissToast,
      paletteOpen,
      openPalette: () => setPaletteOpen(true),
      closePalette: () => setPaletteOpen(false),
    }),
    [toast, confirm, dismissToast, paletteOpen]
  );

  return (
    <OverlayContext.Provider value={value}>
      {children}

      <Modal
        open={!!confirmState}
        onClose={() => (confirmState?.loading ? null : settle(false))}
        title={confirmState?.title}
        confirmLabel={confirmState?.confirmLabel || 'Confirm'}
        cancelLabel={confirmState?.cancelLabel || 'Cancel'}
        destructive={confirmState?.destructive}
        onConfirm={handleConfirm}
        confirmLoading={confirmState?.loading}
      >
        {confirmState?.body}
      </Modal>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {createPortal(
        <div className="sv-toaster" role="status" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className={`sv-toast sv-toast--${t.tone}`}>
              {t.tone === 'success' && <i className="bx bx-check-circle" aria-hidden="true" />}
              {t.tone === 'error' && <i className="bx bx-error-circle" aria-hidden="true" />}
              <span className="sv-toast__msg">{t.message}</span>
              <button
                type="button"
                className="sv-toast__close"
                aria-label="Dismiss"
                onClick={() => dismissToast(t.id)}
              >
                <i className="bx bx-x" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </OverlayContext.Provider>
  );
}
